import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import Navigation from "@/components/Navigation";
import Breadcrumbs from "@/components/Breadcrumbs";
import Footer from "@/components/Footer";
import { useAdmin } from "@/hooks/useAdmin";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Skeleton } from "@/components/ui/skeleton";
import { Trash2, Edit, Plus } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useSEO } from "@/hooks/useSEO";

interface RoadmapFormData {
  title: string;
  description: string;
  status: "planned" | "in_progress" | "completed" | "cancelled";
  category: string;
  quarter: string;
  priority: "low" | "medium" | "high";
  is_public: boolean;
  expected_date: string;
}

const AdminRoadmap = () => {
  useSEO({
    title: 'Admin Roadmap - Ommni',
    description: 'Admin access only',
    noindex: true,
  });
  
  const { isAdmin, loading: adminLoading } = useAdmin();
  const queryClient = useQueryClient();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<RoadmapFormData>({
    title: "",
    description: "",
    status: "planned",
    category: "",
    quarter: "",
    priority: "medium",
    is_public: true,
    expected_date: "",
  });

  const { data: roadmapItems, isLoading } = useQuery({
    queryKey: ["admin-roadmap-items"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("roadmap_items")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    },
    enabled: isAdmin,
  });

  const createMutation = useMutation({
    mutationFn: async (data: RoadmapFormData) => {
      const { error } = await supabase.from("roadmap_items").insert([data]);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-roadmap-items"] });
      queryClient.invalidateQueries({ queryKey: ["roadmap-items"] });
      toast.success("Roadmap item created");
      resetForm();
      setIsDialogOpen(false);
    },
    onError: () => toast.error("Failed to create item"),
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<RoadmapFormData> }) => {
      const { error } = await supabase.from("roadmap_items").update(data).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-roadmap-items"] });
      queryClient.invalidateQueries({ queryKey: ["roadmap-items"] });
      toast.success("Roadmap item updated");
      resetForm();
      setIsDialogOpen(false);
    },
    onError: () => toast.error("Failed to update item"),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("roadmap_items").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-roadmap-items"] });
      queryClient.invalidateQueries({ queryKey: ["roadmap-items"] });
      toast.success("Roadmap item deleted");
    },
    onError: () => toast.error("Failed to delete item"),
  });

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      status: "planned",
      category: "",
      quarter: "",
      priority: "medium",
      is_public: true,
      expected_date: "",
    });
    setEditingId(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingId) {
      updateMutation.mutate({ id: editingId, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleEdit = (item: any) => {
    setFormData({
      title: item.title,
      description: item.description,
      status: item.status as "planned" | "in_progress" | "completed" | "cancelled",
      category: item.category,
      quarter: item.quarter,
      priority: item.priority as "low" | "medium" | "high",
      is_public: item.is_public,
      expected_date: item.expected_date || "",
    });
    setEditingId(item.id);
    setIsDialogOpen(true);
  };

  if (adminLoading) {
    return (
      <div className="min-h-screen">
        <Navigation />
        <div className="container mx-auto pt-32 px-4">
          <Skeleton className="h-12 w-64 mb-8" />
          <Skeleton className="h-64" />
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen">
        <Navigation />
        <div className="container mx-auto pt-32 px-4 text-center">
          <h1 className="text-4xl font-bold mb-4">Access Denied</h1>
          <p className="text-muted-foreground">You don't have permission to access this page.</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Navigation />
      <main className="pt-32 px-4 pb-24">
        <div className="container mx-auto max-w-7xl">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-4xl font-bold mb-2">Manage Roadmap</h1>
              <p className="text-muted-foreground">Create and manage product roadmap items</p>
            </div>
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={resetForm}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Item
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingId ? "Edit" : "Create"} Roadmap Item</DialogTitle>
                  <DialogDescription>
                    {editingId ? "Update" : "Add"} a roadmap item for your product timeline
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="title">Title</Label>
                    <Input
                      id="title"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={4}
                      required
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="status">Status</Label>
                      <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value as "planned" | "in_progress" | "completed" | "cancelled" })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="planned">Planned</SelectItem>
                          <SelectItem value="in_progress">In Progress</SelectItem>
                          <SelectItem value="completed">Completed</SelectItem>
                          <SelectItem value="cancelled">Cancelled</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="priority">Priority</Label>
                      <Select value={formData.priority} onValueChange={(value) => setFormData({ ...formData, priority: value as "low" | "medium" | "high" })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">Low</SelectItem>
                          <SelectItem value="medium">Medium</SelectItem>
                          <SelectItem value="high">High</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="category">Category</Label>
                      <Input
                        id="category"
                        value={formData.category}
                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                        placeholder="e.g., Integration, Feature"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="quarter">Quarter</Label>
                      <Input
                        id="quarter"
                        value={formData.quarter}
                        onChange={(e) => setFormData({ ...formData, quarter: e.target.value })}
                        placeholder="e.g., Q1 2025"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="expected_date">Expected Date</Label>
                    <Input
                      id="expected_date"
                      type="date"
                      value={formData.expected_date}
                      onChange={(e) => setFormData({ ...formData, expected_date: e.target.value })}
                    />
                  </div>
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="is_public"
                      checked={formData.is_public}
                      onCheckedChange={(checked) => setFormData({ ...formData, is_public: checked })}
                    />
                    <Label htmlFor="is_public">Public (visible to everyone)</Label>
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="submit">{editingId ? "Update" : "Create"}</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {isLoading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-32" />
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              {roadmapItems?.map((item) => (
                <Card key={item.id}>
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <CardTitle>{item.title}</CardTitle>
                        <CardDescription className="mt-2">{item.description}</CardDescription>
                        <div className="flex flex-wrap gap-2 mt-4">
                          <span className="text-xs px-2 py-1 rounded bg-primary/10 text-primary">{item.status}</span>
                          <span className="text-xs px-2 py-1 rounded bg-secondary">{item.category}</span>
                          <span className="text-xs px-2 py-1 rounded bg-muted">{item.quarter}</span>
                          <span className="text-xs px-2 py-1 rounded">{item.priority} priority</span>
                          <span className="text-xs px-2 py-1 rounded">{item.votes} votes</span>
                          {!item.is_public && <span className="text-xs px-2 py-1 rounded bg-red-500/10 text-red-500">Private</span>}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => handleEdit(item)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => {
                            if (confirm("Are you sure you want to delete this item?")) {
                              deleteMutation.mutate(item.id);
                            }
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>
      <Breadcrumbs />
      <Footer />
    </div>
  );
};

export default AdminRoadmap;
